import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Users, UserCog, Save, Loader2, CalendarClock, HardHat, Factory, Building2,
  RefreshCw, MapPin, Mail, Phone, Wallet, ExternalLink, Edit3, History, ShieldAlert,
} from "lucide-react";
import { Link } from "react-router-dom";

// Modal that lets CRM view + edit a direct-onboarded project. Handles:
//  · Basic client + project field edits (PATCH /api/crm/design-projects/{id})
//  · Timeline recompute or per-line override
//  · Stakeholder reassignment (POST .../reassign) at any time

const ROLE_SLOTS = [
  { field: "interior_designer_id", role: "Interior Designer", icon: UserCog, label: "Interior Designer" },
  { field: "operations_manager_id", role: "Operations Manager", icon: Users, label: "Operations Manager" },
  { field: "operations_executive_id", role: "Operations Executive", icon: Users, label: "Operations Executive" },
  { field: "project_manager_id", role: "Project Manager", icon: Users, label: "Project Manager" },
  { field: "site_supervisor_id", role: "Site Supervisor", icon: HardHat, label: "Site Supervisor" },
];

export default function ManageOnboardedProjectModal({ projectId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [assign, setAssign] = useState({});
  const [rosters, setRosters] = useState({});
  const [overridePoint, setOverridePoint] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, ...rosterRes] = await Promise.all([
        api.get(`/crm/design-projects/${projectId}`),
        ...ROLE_SLOTS.map(s =>
          api.get(`/users/by-role/${encodeURIComponent(s.role)}`).catch(() => ({ data: [] }))
        ),
      ]);
      setDetail(data);
      setForm({
        client_name: data.client_name || "",
        client_email: data.client?.email || "",
        client_phone: data.client?.phone || "",
        client_address: data.address || "",
        client_city: data.city || "",
        project_name: data.project_name || "",
        project_amount: data.project_amount ?? "",
        booking_amount: data.amount_received ?? 0,
        notes: data.crm_notes || "",
        timeline_start_date: data.timeline?.d0 || "",
      });
      setAssign({
        interior_designer_id: data.interior_designer_id || "",
        operations_manager_id: data.operations_manager_id || "",
        operations_executive_id: data.operations_executive_id || "",
        project_manager_id: data.project_manager_id || "",
        site_supervisor_id: data.site_supervisor_id || "",
      });
      const rMap = {};
      ROLE_SLOTS.forEach((s, i) => { rMap[s.field] = rosterRes[i].data || []; });
      setRosters(rMap);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      onClose?.();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const patch = async () => {
    setSaving(true);
    try {
      const body = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) {
          if (["project_amount", "booking_amount"].includes(k)) body[k] = Number(v);
          else body[k] = v;
        }
      });
      const { data } = await api.patch(`/crm/design-projects/${projectId}`, body);
      setDetail(data);
      toast.success("Project updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const applyReassign = async (partial) => {
    setReassigning(true);
    try {
      const { data } = await api.post(`/crm/design-projects/${projectId}/reassign`, partial);
      setDetail(data);
      setAssign({
        interior_designer_id: data.interior_designer_id || "",
        operations_manager_id: data.operations_manager_id || "",
        operations_executive_id: data.operations_executive_id || "",
        project_manager_id: data.project_manager_id || "",
        site_supervisor_id: data.site_supervisor_id || "",
      });
      toast.success("Team updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setReassigning(false); }
  };

  const timelinePoints = useMemo(() => detail?.timeline?.points || [], [detail]);
  const isExecutionPhase = useMemo(
    () => !!(detail?.production_ready || detail?.execution_project?.id),
    [detail]
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="manage-onboarded-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#262626] bg-[#050505] sticky top-0">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Manage Onboarded Client</p>
            <h2 className="font-display text-xl text-white mt-1">
              {loading ? "Loading…" : `${detail?.client_name || ""}${detail?.city ? ` · ${detail.city}` : ""}`}
            </h2>
            {!loading && detail?.project_name && (
              <p className="text-xs text-gray-500 mt-0.5">{detail.project_name}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="manage-modal-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 text-[#d4af37] animate-spin"/></div>
        ) : (
          <div className="overflow-y-auto p-6 space-y-6">
            {/* Downstream link chips */}
            <div className="flex flex-wrap gap-2" data-testid="manage-downstream-links">
              {detail?.imos_order?.id && (
                <Link to="/imos/progress" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#262626] hover:border-[#d4af37]/60 text-[11px] font-mono text-gray-300 hover:text-[#d4af37] transition-colors" data-testid="link-imos">
                  <Factory className="w-3 h-3"/> Open in IMOS <ExternalLink className="w-3 h-3 opacity-60"/>
                </Link>
              )}
              {detail?.site_project?.id && (
                <Link to="/site/projects" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#262626] hover:border-[#d4af37]/60 text-[11px] font-mono text-gray-300 hover:text-[#d4af37] transition-colors" data-testid="link-site">
                  <Building2 className="w-3 h-3"/> Open in Site Progress <ExternalLink className="w-3 h-3 opacity-60"/>
                </Link>
              )}
              <Link to={`/design/projects/${projectId}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#262626] hover:border-[#d4af37]/60 text-[11px] font-mono text-gray-300 hover:text-[#d4af37] transition-colors" data-testid="link-design">
                <ExternalLink className="w-3 h-3"/> Full Project Page
              </Link>
            </div>

            {/* Client + Project card */}
            <section className="iz-card p-5">
              <SectionTitle icon={Mail} title="Client & Project"/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="Client Name" value={form.client_name} onChange={v => setField("client_name", v)} testid="edit-client-name"/>
                <TextField label="Client Email" value={form.client_email} onChange={v => setField("client_email", v)} testid="edit-client-email"/>
                <TextField label="Client Phone" value={form.client_phone} onChange={v => setField("client_phone", v)} testid="edit-client-phone"/>
                <TextField label="City" value={form.client_city} onChange={v => setField("client_city", v)} testid="edit-client-city"/>
                <div className="md:col-span-2">
                  <TextField label="Address" value={form.client_address} onChange={v => setField("client_address", v)} testid="edit-client-address"/>
                </div>
                <TextField label="Project Name" value={form.project_name} onChange={v => setField("project_name", v)} testid="edit-project-name"/>
                <TextField label="Project Amount (₹)" type="number" value={form.project_amount} onChange={v => setField("project_amount", v)} testid="edit-project-amount"/>
                <TextField label="Booking / Received (₹)" type="number" value={form.booking_amount} onChange={v => setField("booking_amount", v)} testid="edit-booking-amount"/>
                <TextField label="Timeline Start (recomputes 13-line)" type="date" value={form.timeline_start_date} onChange={v => setField("timeline_start_date", v)} testid="edit-timeline-start"/>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Notes</label>
                  <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} className="iz-input" data-testid="edit-notes"/>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={patch} disabled={saving} className="iz-btn-primary flex items-center gap-2" data-testid="edit-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                  Save Changes
                </button>
              </div>
            </section>

            {/* Team assignment card */}
            <section className="iz-card p-5" data-testid="manage-team-card">
              <SectionTitle icon={Users} title="Assigned Team · Reassign Any Time"/>
              <p className="text-[11px] text-gray-500 mb-4">
                Changing the Site Supervisor or Ops Manager mirrors instantly to the Site Progress tab.
                Leave blank to unassign.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ROLE_SLOTS.map(slot => (
                  <RoleSlot key={slot.field}
                             slot={slot}
                             value={assign[slot.field]}
                             options={rosters[slot.field] || []}
                             disabled={reassigning}
                             onChange={(v) => setAssign(a => ({ ...a, [slot.field]: v }))}
                             onApply={() => applyReassign({ [slot.field]: assign[slot.field] })}
                             testid={`reassign-${slot.field}`}/>
                ))}
              </div>
            </section>

            {/* Timeline card */}
            {timelinePoints.length > 0 && (
              <section className="iz-card p-5" data-testid="manage-timeline-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-[#d4af37]"/>
                    <h3 className="font-display text-lg text-white">13-Line Timeline (current)</h3>
                  </div>
                  <button onClick={() => setShowHistory(true)}
                           className="iz-btn-secondary text-xs flex items-center gap-1"
                           data-testid="timeline-history-open">
                    <History className="w-3 h-3"/> Override History
                  </button>
                </div>
                {isExecutionPhase ? (
                  <p className="text-[11px] text-gray-500 mb-3">
                    Execution phase active — click <span className="text-[#d4af37]">Override</span> on any row to change the date with an audited reason.
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-400/80 mb-3 flex items-center gap-1.5">
                    <ShieldAlert className="w-3 h-3"/> Timeline overrides unlock once the project enters the Execution phase.
                  </p>
                )}
                <div className="border border-[#262626] rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#0f0f0f] border-b border-[#262626]">
                        {["#", "Milestone", "Date", "Overridden", ""].map(h =>
                          <th key={h} className="text-[10px] font-mono uppercase tracking-widest text-gray-500 py-2.5 px-4 text-left">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {timelinePoints.map((pt, idx) => (
                        <tr key={pt.key} className="border-b border-[#1a1a1a]" data-testid={`timeline-point-${pt.key}`}>
                          <td className="py-2 px-4 text-xs text-gray-500 font-mono">{idx + 1}</td>
                          <td className="py-2 px-4 text-white text-sm">{pt.label}</td>
                          <td className="py-2 px-4 text-[#d4af37] font-mono text-xs">{pt.date}</td>
                          <td className="py-2 px-4 text-[10px] font-mono text-gray-500" title={pt.override_reason || ""}>
                            {pt.overridden ? "yes" : "-"}
                          </td>
                          <td className="py-2 px-4 text-right">
                            <button
                              disabled={!isExecutionPhase}
                              onClick={() => setOverridePoint(pt)}
                              className="iz-btn-secondary text-[11px] flex items-center gap-1 ml-auto disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`timeline-override-${pt.key}`}
                            >
                              <Edit3 className="w-3 h-3"/> Override
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-gray-500 mt-3">Or update start date above and click <span className="text-white">Save Changes</span> to recompute the entire timeline.</p>
              </section>
            )}
          </div>
        )}
      </div>

      {overridePoint && (
        <TimelineOverrideDialog
          point={overridePoint}
          projectId={projectId}
          onClose={() => setOverridePoint(null)}
          onDone={() => { setOverridePoint(null); load(); onSaved?.(); }}
        />
      )}
      {showHistory && (
        <TimelineHistoryDialog
          projectId={projectId}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-4 h-4 text-[#d4af37]"/>}
      <h3 className="font-display text-lg text-white">{title}</h3>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="iz-input" data-testid={testid}/>
    </div>
  );
}

function RoleSlot({ slot, value, options, disabled, onChange, onApply, testid }) {
  const Icon = slot.icon;
  return (
    <div className="border border-[#262626] rounded-md p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-[#d4af37]"/>
        <p className="text-[11px] font-mono uppercase tracking-widest text-gray-400">{slot.label}</p>
      </div>
      <div className="flex gap-2">
        <select value={value} onChange={(e) => onChange(e.target.value)} className="iz-input flex-1 text-sm" data-testid={testid}>
          <option value="">{`-- Unassigned --`}</option>
          {options.map(u => <option key={u.id} value={u.id}>{`${u.name} · ${u.email}`}</option>)}
        </select>
        <button onClick={onApply} disabled={disabled}
                 className="iz-btn-secondary text-xs flex items-center gap-1"
                 data-testid={`${testid}-apply`}>
          <RefreshCw className="w-3 h-3"/> Apply
        </button>
      </div>
    </div>
  );
}


function TimelineOverrideDialog({ point, projectId, onClose, onDone }) {
  const [newDate, setNewDate] = useState(point?.date || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!newDate) return toast.error("Pick a new date");
    if ((reason || "").trim().length < 3) {
      return toast.error("A reason (min 3 characters) is required");
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/crm/design-projects/${projectId}/timeline/override`,
        { point_key: point.key, new_date: newDate, reason: reason.trim() },
      );
      if (data.no_change) toast.info("Date unchanged - no override recorded");
      else toast.success("Timeline override recorded · Ops Manager + Client notified");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="timeline-override-dialog">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Timeline Override</p>
            <h3 className="font-display text-lg text-white mt-1">{point.label}</h3>
            <p className="text-[11px] font-mono text-gray-500 mt-0.5">Current: {point.date}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="timeline-override-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">New date *</label>
            <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                    className="iz-input" data-testid="timeline-override-date"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Reason for change *</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                       placeholder="e.g. Client travel · vendor delay · scope revision..."
                       className="iz-input" data-testid="timeline-override-reason"/>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="iz-btn-secondary" data-testid="timeline-override-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="iz-btn-primary flex items-center gap-2" data-testid="timeline-override-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            Save Override
          </button>
        </div>
      </div>
    </div>
  );
}

function TimelineHistoryDialog({ projectId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/crm/design-projects/${projectId}/timeline/history`)
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [projectId]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="timeline-history-dialog">
      <div className="bg-[#0a0a0a] border border-[#262626] rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Override History</p>
            <h3 className="font-display text-lg text-white mt-1">Timeline audit trail</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="timeline-history-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="overflow-y-auto p-5">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No overrides yet. All milestones are on their auto-computed dates.</p>
          ) : (
            <div className="space-y-3">
              {rows.map(row => (
                <div key={row.id} className="border border-[#262626] rounded-md p-3" data-testid={`history-row-${row.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm text-white">{row.point_label}</p>
                    <p className="text-[10px] font-mono text-gray-500">{new Date(row.changed_at).toLocaleString()}</p>
                  </div>
                  <p className="text-[11px] font-mono text-gray-400">
                    <span className="line-through text-gray-500">{row.old_date || "unset"}</span>
                    <span className="text-gray-500 mx-1.5">→</span>
                    <span className="text-[#d4af37]">{row.new_date}</span>
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    <span className="text-gray-400">Reason:</span> {row.reason}
                  </p>
                  <p className="text-[10px] font-mono text-gray-600 mt-1">
                    by {row.changed_by_user_name || "unknown"} ({row.changed_by_role || "-"})
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
